import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';

const COUNT_INTERVAL_MS = 10000;
const QUOTA_INTERVAL_MS = 5 * 60 * 1000;

const SPAM_NAMES = ['INBOX.spam', 'INBOX.Spam', 'INBOX.Junk', 'Junk', 'Spam'];

// Klasör sayaçlarını (okunmamış/toplam), yeni mail bildirimini ve disk kotasını yönetir.
// Dashboard seviyesinde çalışır; böylece Kişiler/Takvim sayfalarında da yeni mail sesi gelir.
export const useFolderCounts = () => {
  const [counts, setCounts] = useState({});
  const [quota, setQuota] = useState(null); // { used_kb, limit_kb } | null
  const prevInboxRef = useRef(null);
  const soundRef = useRef(null);
  const audioUnlockedRef = useRef(false);

  // Bildirim sesi + tarayıcı bildirim izni
  useEffect(() => {
    soundRef.current = new Audio('/sounds/mail-notification.mp3');
    soundRef.current.volume = 0.5;
    soundRef.current.load();

    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    // Tarayıcı autoplay politikası: ilk kullanıcı etkileşiminde sesi "aç"
    const unlock = () => {
      if (audioUnlockedRef.current || !soundRef.current) return;
      soundRef.current.play().then(() => {
        soundRef.current.pause();
        soundRef.current.currentTime = 0;
        audioUnlockedRef.current = true;
      }).catch(() => {});
    };
    document.addEventListener('click', unlock, { once: true });
    document.addEventListener('keydown', unlock, { once: true });
    document.addEventListener('touchstart', unlock, { once: true });
    return () => {
      document.removeEventListener('click', unlock);
      document.removeEventListener('keydown', unlock);
      document.removeEventListener('touchstart', unlock);
    };
  }, []);

  const playSound = () => {
    const audio = soundRef.current;
    if (!audio) return;
    audio.currentTime = 0;
    const promise = audio.play();
    if (promise !== undefined) {
      promise.catch(() => { audioUnlockedRef.current = false; });
    }
  };

  const fetchCounts = useCallback(async () => {
    try {
      const folders = await api.getFolders();
      const next = {};
      let inboxKnown = false;
      folders.forEach((folder) => {
        // Sayı null ise (sunucu o an okuyamadı) bu klasör için değer yazılmaz: eski sayı korunur
        if (folder.name === 'INBOX') {
          if (folder.unread != null) {
            next.inbox = folder.unread;
            inboxKnown = true;
          }
        } else if (folder.name === 'INBOX.Drafts' || folder.name === 'Drafts') {
          if (folder.total != null) next.drafts = folder.total; // taslaklarda toplam gösterilir
        } else if (SPAM_NAMES.includes(folder.name)) {
          if (folder.unread != null) next.spam = folder.unread;
        }
      });

      // Yeni mail geldi mi? Yalnızca gerçekten okunabilmiş bir önceki değere göre karşılaştır
      // (ref kullanıyoruz: interval içindeki eski state'e takılmasın)
      if (inboxKnown) {
        const inbox = next.inbox;
        if (prevInboxRef.current !== null && inbox > prevInboxRef.current) {
          playSound();
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('Yeni Mail', {
              body: `${inbox - prevInboxRef.current} yeni mail geldi`,
              icon: '/favicon.ico',
            });
          }
        }
        prevInboxRef.current = inbox;
      }
      setCounts((prev) => ({ ...prev, ...next }));
    } catch (err) {
      console.error('Failed to fetch folder counts:', err);
    }
  }, []);

  // Zamanlanmış e-posta sayısı veritabanından gelir (IMAP'ten bağımsız, hafif)
  const fetchScheduledCount = useCallback(async () => {
    try {
      const res = await api.getScheduled();
      setCounts((prev) => ({ ...prev, scheduled: res.pending_count || 0 }));
    } catch (err) {
      // sayaç gösterilmez
    }
  }, []);

  const fetchQuota = useCallback(async () => {
    try {
      const data = await api.getQuota();
      setQuota(data && data.limit_kb ? data : null);
    } catch (err) {
      setQuota(null); // kota desteklenmiyorsa ölçer gizlenir
    }
  }, []);

  useEffect(() => {
    fetchCounts();
    fetchScheduledCount();
    const id = setInterval(() => { fetchCounts(); fetchScheduledCount(); }, COUNT_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchCounts, fetchScheduledCount]);

  useEffect(() => {
    fetchQuota();
    const id = setInterval(fetchQuota, QUOTA_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchQuota]);

  const refreshCounts = useCallback(() => { fetchCounts(); fetchScheduledCount(); }, [fetchCounts, fetchScheduledCount]);

  return { counts, quota, refreshCounts };
};

export default useFolderCounts;
